export const PATIENT_API = {
  app: {
    base: '/app',
    layout: '/layout'
  },
  auth: {
    base: '/auth',
    login: '/login',
    forgot: '/forgot',
    reset: '/reset',
    loginWithVerifyLink: '/verify-login'
  },
  users: {
    base: '/users',
    getCustomerInfo: '/customer',
    getAccount: '/account',
    editProfile: '/profile',
    changePassword: '/change-password',
    getStripeDashboardUrl: '/billing/url',
    getPaymentMethods: '/payment-methods',
    addPaymentMethods: '/payment-methods',
    removePaymentMethods: '/payment-methods/:id',
    setDefaultPaymentMethod: '/payment-methods/:id/default',
    addDefaultAddress: '/default-address',
    getAddresses: '/addresses',
    editAddresses: '/addresses',
    pickupPharmacy: '/pickup-pharmacy',
    pharmacies: '/pharmacies',
    requirements: '/requirements',
    // getIdentity: '/identity', // get patient by EMR ID; this is deprecated
    identifiers: '/identifiers',
    singleIdentity: '/identifiers/:uid',
    personaLink: '/persona-link',
    uploadIDs: '/upload-ids',
    govIdStatus: '/gov-id/status',
    personaStatus: '/persona-status',
    check: '/check',
    resendVerificationEmail: '/resend-verification-email',
    submitIdVerification: '/submit-id-verification',
    drugCards: '/drug-cards',
    singleDrugCard: '/drug-cards/:uid',
    drugCardFrontViewUrl: '/drug-cards/:uid/front/view-url',
    drugCardBackViewUrl: '/drug-cards/:uid/back/view-url'
  },
  messages: {
    base: '/messages',
    mailInbound: '/mail/inbound',
    smsInbound: '/sms/inbound',
    webInbound: '/web/inbound',
    smsStatus: '/sms/status',
    alertTeam: '/alert/:team'
  },
  products: {
    base: '/products',
    upsell: '/upsell',
    single: '/:id'
  },
  orders: {
    base: '/orders',
    getOrdersHistory: '/history', // deprecated
    getSubscriptions: '/subscriptions',
    getOrderDetails: '/:id',
    setShippingAddress: '/shipping-address',
    updateShippingAddress: '/:id/shipping-address',
    saveAddresses: '/addresses',
    payInvoice: '/invoice/:id/pay',
    payments: '/:orderId/payments'
  },
  ordersV2: {
    base: '/v2/orders',
    single: '/:id',
    setShippingAddress: '/shipping-address',
    saveAddresses: '/addresses',
    payInvoice: '/invoice/:id/pay'
  },
  checkouts: {
    base: '/checkouts',
    single: '/:id',
    shippingAddress: '/shipping-address',
    coupons: '/addresses',
    payInvoice: '/pay'
  },
  prescriptions: {
    base: '/prescriptions',
    products: '/products'
  },
  consultations: {
    base: '/consultations',
    getRequiredForms: '/required-forms',
    /** Pathwai-aware variant of /required-forms (adds per-questionnaire routing). */
    pendingRequirements: '/pending-requirements',
    draft: '/draft',
    latestByType: '/:type/latest',
    /**
     * Server-driven runner. The flow is evaluated on the server and the client
     * is handed one question at a time, so it never needs the question catalog.
     */
    runner: '/runner',
    runnerAnswer: '/runner/answer',
    runnerBack: '/runner/back',
    /**
     * Presigned destinations for a question's file uploads. Bytes go straight
     * from the browser to S3 — patient-api runs on Lambda and would otherwise
     * hit the request payload limit on a phone photo.
     */
    runnerUploadUrl: '/runner/upload-url',
    /**
     * Files attached to one consultation.
     *
     * POST creates presigned destinations for a question's uploads; GET returns
     * presigned URLs for what is already stored, so a returning patient sees
     * their upload rather than an empty picker. Bytes go straight from the
     * browser to S3 — patient-api runs on Lambda and would otherwise hit the
     * request payload limit on a phone photo.
     */
    files: '/:id/files'
  },

  /**
   * Questionnaire definitions. The patient app evaluates the flow in the
   * browser, so it needs the questions and nodes, not one question at a time.
   */
  questionnaires: {
    base: '/questionnaires'
  },

  files: {
    base: '/files',
    single: '/single',
    uploadUrl: '/upload-url'
  },
  appointments: {
    base: '/appointments',
    getPosition: '/:id/position'
  },
  publicAppointments: {
    base: '/appointments',
    getPosition: '/:id/position'
  },
  notifications: {
    base: '/notifications',
    getNotification: '/:type'
  },
  labReq: {
    base: '/lab-req'
  },
  logs: {
    base: '/logs',
    weightLogs: '/weight',
    singleWeightLog: '/weight/:id',
    injectionLogs: '/injections',
    singleInjectionLog: '/injections/:id',
    allLogs: '/all',
    recentActivity: '/activity/recent'
  },
  chat: {
    base: '/chat',
    join: '/join',
    getToken: '/token',
    refreshToken: '/token/refresh',
    // ── Chat widget surface (ported from jackjill chat-api) ──────────────────
    /** Optional-auth probe used by the chat widget on load. */
    auth: '/auth',
    /** Medical-inbox messages surfaced in the widget. */
    messages: '/messages',
    messagesRead: '/messages/read',
    typing: '/typing',
    intakePendingQuestions: '/intake/pending-questions',
    files: '/files/:fileName',
    /** Async support tickets (Mongo). */
    tickets: '/tickets',
    ticketMessages: '/tickets/:id/messages',
    /** AI conversation (buffered UI-message stream) + feedback. */
    conversation: '/conversation',
    conversationFeedback: '/conversation/feedback'
  },
  aiSummary: {
    base: '/ai-summary',
    latest: '/latest',
    generate: '/generate',
    check: '/check',
    start: '/start',
    ensure: '/ensure',
    rating: '/:id/rating'
  },
  checkout: {
    base: '/checkout',
    start: '/start/:id',
    items: '/items/:id',
    coupon: '/:id/coupon/:code'
  },
  checkoutV2: {
    base: '/v2/checkout',
    single: '/:id',
    start: '/:id/start',
    coupon: '/:id/coupon/:code', // deprecated use GET /:id?coupon=:code
    finalize: '/:id/finalize', //deprecated use POST /payments
    payments: '/:id/payments',
    /** Opens a gateway session for checkouts where the gateway UI collects payment. */
    paymentSession: '/:id/payment-session',
    /**
     * Persists shipping / pharmacy selection on the cart without opening a new
     * gateway session (so embedded checkouts like Whop do not remount).
     */
    delivery: '/:id/delivery',
    pharmacies: '/pharmacies',
    transferOut: '/:id/transfer-out'
  },
  tasks: {
    base: '/tasks'
  },
  subscriptions: {
    base: '/subscriptions',
    single: '/:id',
    renew: '/:id/renew',
    history: '/:id/history',
    skipCycle: '/:id/skip-cycle'
  },
  purchases: {
    base: '/purchases',
    history: '/:id/history',
    single: '/:orderId/:productId'
  },
  labResults: {
    base: '/lab-results',
    upload: '/upload',
    uploadUrl: '/upload-url',
    getLatestLabResultOfOrder: '/order/:orderId/latest',
    pdf: '/:id/pdf'
  },
  giftCards: {
    base: '/gift-cards'
  }
};

/**
 * Named Native-mode Firestore database for chat projections.
 * `portals-care` `(default)` is Datastore mode and rejects the Cloud Firestore API.
 */
export const FIRESTORE_CHAT_DATABASE_ID = 'chat';
